import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { Title, Meta } from '@angular/platform-browser';
import { SeoService } from '../seo.service';
import * as AOS from 'aos';

// ─── Interfaces ───────────────────────────────────────────────────────────────
interface Feature {
  title: string;
  desc: string;
}

interface FaqQA {
  question: string;
  answer: string;
}

interface ServiceData {
  title_esp: string;
  title_eng: string;
  subtitle_esp: string;
  subtitle_eng: string;
  intro_esp: string;
  intro_eng: string;
  icon: string;
  features_esp: Feature[];
  features_eng: Feature[];
  faq_esp: FaqQA[];
  faq_eng: FaqQA[];
}

interface Translations {
  breadcrumb_home: string;
  label_services: string;
  faq_label: string;
  faq_title: string;
  cta_label: string;
  cta_title: string;
  cta_btn: string;
  nav_quienes: string;
  nav_servicios: string;
  nav_trabajos: string;
  nav_contacto: string;
  nav_metodologia: string;
  footer_sub: string;
  footer_nav: string;
  footer_services: string;
  footer_rights: string;
}

@Component({
  selector: 'ServiciosComponent',
  templateUrl: './Servicios.Component.html',
  styleUrls: ['./Servicios.component.scss'],
})
export class ServiciosComponent implements OnInit, OnDestroy {

  // ─── State (compatibles con tu código original) ────────────────────────────
  currentJustify = 'justified';
  panelOpenState = false;
  ecomm = 1;
  event: string;
  openFaqIndex = -1;

  lang: 'ESP' | 'ENG' = 'ESP';
  t: Translations = {} as Translations;

  // ─── Translations ──────────────────────────────────────────────────────────
  private translations: Record<'ESP' | 'ENG', Translations> = {
    ESP: {
      breadcrumb_home: 'Servicios',
      label_services:  'Patagonia Chain',
      faq_label:       'Preguntas frecuentes',
      faq_title:       'Lo que más preguntan sobre este servicio',
      cta_label:       '¿Te interesa este servicio?',
      cta_title:       'Hablemos sobre<br><em>tu proyecto</em>',
      cta_btn:         'Escribinos',
      nav_quienes:     'Quiénes somos',
      nav_servicios:   'Servicios',
      nav_trabajos:    'Trabajos',
      nav_contacto:    'Contacto',
      nav_metodologia: 'Cómo trabajamos',
      footer_sub:      'Desarrollo de Software',
      footer_nav:      'Navegación',
      footer_services: 'Servicios',
      footer_rights:   'Todos los derechos reservados.',
    },
    ENG: {
      breadcrumb_home: 'Services',
      label_services:  'Patagonia Chain',
      faq_label:       'FAQ',
      faq_title:       'Frequently asked questions about this service',
      cta_label:       'Interested in this service?',
      cta_title:       "Let's talk about<br><em>your project</em>",
      cta_btn:         'Get in touch',
      nav_quienes:     'About us',
      nav_servicios:   'Services',
      nav_trabajos:    'Works',
      nav_contacto:    'Contact',
      nav_metodologia: 'How we work',
      footer_sub:      'Software Development',
      footer_nav:      'Navigation',
      footer_services: 'Services',
      footer_rights:   'All rights reserved.',
    },
  };

  // ─── Service data ──────────────────────────────────────────────────────────
  private services: Record<number, ServiceData> = {

    1: {
      title_esp: 'E-Commerce',
      title_eng: 'E-Commerce',
      subtitle_esp: 'Plataformas de venta diseñadas para diferenciarte y crecer globalmente.',
      subtitle_eng: 'Sales platforms designed to differentiate your brand and grow globally.',
      intro_esp: 'Desarrollamos plataformas de e-commerce completamente personalizadas, diseñadas para adaptarse a las necesidades específicas de tu negocio. Nuestras soluciones permiten a las empresas optimizar sus procesos de venta en línea, diferenciándose en un mercado competitivo y alcanzando una audiencia global. Nos enfocamos en crear experiencias de compra únicas que reflejen la identidad de tu marca y maximicen la conversión.',
      intro_eng: 'We develop fully customized e-commerce platforms designed to adapt to the specific needs of your business. Our solutions enable companies to optimize their online sales processes, stand out in a competitive market, and reach a global audience. We focus on creating unique shopping experiences that reflect your brand identity and maximize conversion.',
      icon: '../../assets/chain/Asset 17.png',
      features_esp: [
        {
          title: 'Diseño UX & UI',
          desc: 'Nos especializamos en el diseño de interfaces que no solo son visualmente atractivas, sino que también son intuitivas y fáciles de navegar. Creamos un recorrido de usuario optimizado que guía a los clientes de manera fluida desde la exploración hasta la compra, mejorando la satisfacción y fomentando la lealtad a largo plazo.',
        },
        {
          title: 'Integraciones',
          desc: 'Conectamos tu tienda con las herramientas que ya usás: sistemas de gestión de inventario, CRMs, pasarelas de pago y soluciones de marketing digital. Estas integraciones garantizan que todos los aspectos de tu negocio en línea funcionen de manera armoniosa y eficiente, reduciendo fricciones operativas.',
        },
        {
          title: 'Enfoque en resultados',
          desc: 'Nuestra experiencia en e-commerce nos permite ofrecer soluciones que superan los estándares del mercado. Nos aseguramos de que tu tienda sea rápida, segura y fácil de gestionar, con características avanzadas como carrito de compras, opciones de pago diversas y optimización completa para dispositivos móviles.',
        },
        {
          title: 'CMS Personalizado',
          desc: 'Desarrollamos sistemas de gestión de contenidos a medida que te permiten controlar todos los aspectos de tu tienda sin conocimientos técnicos avanzados. Facilitamos la actualización de productos, gestión de inventarios y publicación de contenido nuevo de manera ágil y autónoma.',
        },
        {
          title: 'Plataformas a Medida',
          desc: 'Más allá de una tienda en línea estándar, creamos plataformas de e-commerce que se integran profundamente con los sistemas y procesos de tu empresa. Desde soluciones B2B hasta marketplaces complejos, todo diseñado para soportar las operaciones específicas de tu industria.',
        },
      ],
      features_eng: [
        {
          title: 'UX & UI Design',
          desc: 'We specialize in designing interfaces that are not only visually compelling but also intuitive and easy to navigate. We create an optimized user journey that guides customers seamlessly from exploration to purchase, improving satisfaction and fostering long-term loyalty.',
        },
        {
          title: 'Integrations',
          desc: 'We connect your store with the tools you already use: inventory management systems, CRMs, payment gateways, and digital marketing solutions. These integrations ensure that every aspect of your online business runs harmoniously and efficiently, reducing operational friction.',
        },
        {
          title: 'Results-driven approach',
          desc: 'Our e-commerce expertise allows us to deliver solutions that exceed market standards. We ensure your store is fast, secure, and easy to manage — with advanced features like shopping carts, multiple payment options, and full mobile optimization.',
        },
        {
          title: 'Custom CMS',
          desc: 'We develop tailored content management systems that let you control every aspect of your store without advanced technical knowledge. Easily update products, manage inventory, and publish new content quickly and independently.',
        },
        {
          title: 'Custom Platforms',
          desc: 'Beyond a standard online store, we build e-commerce platforms that deeply integrate with your company\'s systems and processes. From B2B solutions to complex marketplaces, all designed to support the specific operations of your industry.',
        },
      ],
      faq_esp: [
        {
          question: '¿Qué diferencia hay entre una tienda a medida y usar Tiendanube o Shopify?',
          answer: 'Una plataforma a medida se adapta al 100% a tus procesos (integraciones con tu sistema de gestión, CRM o pasarelas de pago específicas) en vez de forzarte a las limitaciones de una plantilla estándar. Es la opción indicada cuando tu operación (B2B, marketplaces, catálogos complejos) supera lo que ofrecen las plataformas genéricas.',
        },
        {
          question: '¿Puedo integrar mi tienda con mi sistema de stock, CRM o pasarela de pago?',
          answer: 'Sí, conectamos tu tienda con las herramientas que ya usás: sistemas de gestión de inventario, CRMs, pasarelas de pago y soluciones de marketing digital, para que todo tu negocio online funcione de forma armoniosa.',
        },
        {
          question: '¿Necesito conocimientos técnicos para administrar la tienda?',
          answer: 'No. Desarrollamos un CMS a medida que te permite actualizar productos, gestionar inventario y publicar contenido nuevo de forma ágil y autónoma, sin conocimientos técnicos avanzados.',
        },
        {
          question: '¿Hacen tiendas B2B o marketplaces, no solo B2C?',
          answer: 'Sí. Además de una tienda estándar, creamos plataformas de e-commerce que se integran profundamente con los procesos de tu empresa, desde soluciones B2B hasta marketplaces complejos.',
        },
        {
          question: '¿El diseño de la tienda se adapta a mi marca?',
          answer: 'Sí, nos enfocamos en crear experiencias de compra únicas que reflejen la identidad de tu marca, con un diseño UX/UI pensado para maximizar la conversión.',
        },
      ],
      faq_eng: [
        {
          question: 'What\'s the difference between a custom store and using Tiendanube or Shopify?',
          answer: 'A custom platform adapts 100% to your processes (integrations with your management system, CRM, or specific payment gateways) instead of forcing you into the limitations of a standard template. It\'s the right choice when your operation (B2B, marketplaces, complex catalogs) outgrows what generic platforms offer.',
        },
        {
          question: 'Can I integrate my store with my inventory system, CRM, or payment gateway?',
          answer: 'Yes, we connect your store with the tools you already use: inventory management systems, CRMs, payment gateways, and digital marketing solutions, so every part of your online business runs harmoniously.',
        },
        {
          question: 'Do I need technical knowledge to manage the store?',
          answer: 'No. We build a custom CMS that lets you update products, manage inventory, and publish new content quickly and independently, with no advanced technical knowledge required.',
        },
        {
          question: 'Do you build B2B stores or marketplaces, not just B2C?',
          answer: 'Yes. Beyond a standard store, we build e-commerce platforms that deeply integrate with your company\'s processes, from B2B solutions to complex marketplaces.',
        },
        {
          question: 'Is the store design adapted to my brand?',
          answer: 'Yes, we focus on creating unique shopping experiences that reflect your brand identity, with UX/UI design built to maximize conversion.',
        },
      ],
    },

    2: {
      title_esp: 'Desarrollo Web',
      title_eng: 'Web Development',
      subtitle_esp: 'Sitios, apps y plataformas que conectan tu empresa con el mundo.',
      subtitle_eng: 'Sites, apps and platforms that connect your company with the world.',
      intro_esp: 'Creamos sitios web, aplicaciones y plataformas personalizadas que permiten a las empresas tener un impacto global y conectar de manera efectiva con su audiencia. Nuestra experiencia abarca desde el desarrollo de soluciones simples hasta sistemas complejos, todo diseñado para ayudar a tu empresa a destacar en un mercado digital en constante evolución.',
      intro_eng: 'We create websites, applications, and custom platforms that allow companies to have a global impact and connect effectively with their audience. Our expertise spans from simple solutions to complex systems, all designed to help your business stand out in a constantly evolving digital market.',
      icon: '../../assets/chain/Asset 20.png',
      features_esp: [
        {
          title: 'Diseño Web',
          desc: 'Nos especializamos en crear diseños web modernos, visualmente atractivos y funcionales que capturan la atención desde el primer segundo. Cada elemento visual es cuidadosamente diseñado para mejorar la experiencia del usuario, facilitar la navegación y reflejar fielmente la identidad de tu marca.',
        },
        {
          title: 'Enfoque en calidad',
          desc: 'Desarrollamos sitios web robustos, escalables y seguros utilizando las tecnologías más avanzadas del mercado. Garantizamos que tu sitio funcione de manera eficiente, sea rápido y esté optimizado para todos los dispositivos. Cada proyecto cumple con los más altos estándares de calidad técnica.',
        },
        {
          title: 'Webs Institucionales',
          desc: 'Desarrollamos webs institucionales que representan de manera profesional a tu organización. Estas plataformas comunican tu misión, visión y valores de forma clara y efectiva, transmitiendo seriedad y prestigio a todos tus visitantes.',
        },
        {
          title: 'Mobile Apps',
          desc: 'Desarrollamos aplicaciones móviles nativas y multiplataforma que permiten a las empresas estar siempre al alcance de sus clientes. Nuestras soluciones móviles están optimizadas para un rendimiento superior y una experiencia de usuario excepcional en todos los dispositivos.',
        },
        {
          title: 'Landing & Promo Websites',
          desc: 'Creamos páginas de aterrizaje y sitios promocionales diseñados para campañas de marketing, lanzamiento de productos o eventos especiales. Estas plataformas están optimizadas para captar leads, fomentar conversiones y alcanzar tus objetivos de negocio de manera directa.',
        },
      ],
      features_eng: [
        {
          title: 'Web Design',
          desc: 'We specialize in creating modern, visually compelling, and functional web designs that capture attention from the first second. Every visual element is carefully crafted to improve user experience, simplify navigation, and faithfully reflect your brand identity.',
        },
        {
          title: 'Quality-first approach',
          desc: 'We build robust, scalable, and secure websites using the most advanced technologies available. We ensure your site performs efficiently, loads fast, and is fully optimized for every device — meeting the highest technical quality standards.',
        },
        {
          title: 'Institutional Websites',
          desc: 'We develop institutional websites that professionally represent your organization. These platforms clearly communicate your mission, vision, and values — conveying seriousness and prestige to every visitor.',
        },
        {
          title: 'Mobile Apps',
          desc: 'We develop native and cross-platform mobile applications that keep your business always within reach of your customers. Our mobile solutions are optimized for superior performance and an exceptional user experience across all devices.',
        },
        {
          title: 'Landing & Promo Websites',
          desc: 'We create landing pages and promotional sites designed specifically for marketing campaigns, product launches, or special events. These platforms are optimized for lead capture, conversion, and achieving your business objectives directly and efficiently.',
        },
      ],
      faq_esp: [
        {
          question: '¿Qué diferencia hay entre una landing page, un sitio institucional y una app?',
          answer: 'Una landing está pensada para una campaña puntual y captar leads; un sitio institucional comunica la misión, visión y valores de tu organización de forma permanente; y una app mobile mantiene tu negocio siempre al alcance de tus clientes. Desarrollamos los tres formatos según lo que necesite tu proyecto.',
        },
        {
          question: '¿El sitio va a estar optimizado para celular y para buscadores?',
          answer: 'Sí, construimos sitios rápidos, seguros y optimizados para todos los dispositivos, cumpliendo con altos estándares de calidad técnica.',
        },
        {
          question: '¿Desarrollan aplicaciones móviles nativas o multiplataforma?',
          answer: 'Ambas. Desarrollamos apps móviles nativas y multiplataforma optimizadas para un rendimiento superior y una experiencia de usuario excepcional en todos los dispositivos.',
        },
        {
          question: '¿Trabajan con empresas de Rosario o de todo el país?',
          answer: 'Tenemos base en Rosario, pero trabajamos con empresas de distintas provincias y sectores en todo el país.',
        },
        {
          question: '¿Qué tipo de sitios desarrollan?',
          answer: 'Desde webs institucionales hasta landing pages promocionales y plataformas complejas, todo a medida de tu marca y tus objetivos de negocio.',
        },
      ],
      faq_eng: [
        {
          question: 'What\'s the difference between a landing page, an institutional site, and an app?',
          answer: 'A landing page is built for a specific campaign to capture leads; an institutional site permanently communicates your organization\'s mission, vision, and values; and a mobile app keeps your business within reach of your customers. We build all three formats depending on what your project needs.',
        },
        {
          question: 'Will the site be optimized for mobile and search engines?',
          answer: 'Yes, we build fast, secure sites optimized for every device, meeting high technical quality standards.',
        },
        {
          question: 'Do you build native or cross-platform mobile apps?',
          answer: 'Both. We build native and cross-platform mobile apps optimized for superior performance and an exceptional user experience across all devices.',
        },
        {
          question: 'Do you work with companies from Rosario or across the country?',
          answer: 'We\'re based in Rosario, but we work with companies from different provinces and industries across the country.',
        },
        {
          question: 'What kind of sites do you build?',
          answer: 'From institutional websites to promotional landing pages and complex platforms, all tailored to your brand and business goals.',
        },
      ],
    },

    3: {
      title_esp: 'Desarrollo de Software',
      title_eng: 'Software Development',
      subtitle_esp: 'Automatizamos y optimizamos tus procesos con tecnología de vanguardia.',
      subtitle_eng: 'We automate and optimize your processes with cutting-edge technology.',
      intro_esp: 'Robotizamos procesos de manera inteligente utilizando datos y análisis predictivos para reducir errores humanos y aumentar la productividad. Nuestra experiencia en automatización permite a las empresas eliminar tareas repetitivas y enfocarse en actividades estratégicas que generan valor real. Con soluciones diseñadas a medida, transformamos digitalmente tu negocio logrando eficiencia y precisión en cada proceso.',
      intro_eng: 'We intelligently automate processes using data and predictive analytics to reduce human error and increase productivity. Our automation expertise helps companies eliminate repetitive tasks and focus on strategic value-generating activities. With tailored solutions, we digitally transform your business — achieving efficiency and precision in every process.',
      icon: '../../assets/chain/Asset 18.png',
      features_esp: [
        {
          title: 'Discovery Process',
          desc: 'El primer paso de nuestra metodología: trabajamos en estrecha colaboración con tu equipo para identificar y mapear los procesos con mayor potencial de automatización. Analizamos flujos de trabajo, detectamos cuellos de botella y diseñamos soluciones perfectamente alineadas con los objetivos de tu negocio.',
        },
        {
          title: 'Process Analytics',
          desc: 'Evaluamos el rendimiento actual de tus procesos y establecemos métricas clave que guían el diseño de la automatización. Este análisis detallado nos permite comprender los patrones operativos y predecir con precisión cómo la automatización impactará en la eficiencia global de tu empresa.',
        },
        {
          title: 'Testing',
          desc: 'Antes de implementar cualquier solución, llevamos a cabo pruebas rigurosas bajo distintos escenarios y condiciones operativas. Identificamos posibles puntos de fallo y los resolvemos antes del lanzamiento, garantizando una integración sin fricciones desde el primer día.',
        },
        {
          title: 'Implementación',
          desc: 'Desplegamos las soluciones automatizadas en producción con una transición fluida y sin interrupciones. Proporcionamos soporte continuo y capacitación a tu equipo para una adopción rápida, minimizando el tiempo de inactividad y maximizando el retorno de la inversión desde el primer momento.',
        },
      ],
      features_eng: [
        {
          title: 'Discovery Process',
          desc: 'The first step of our methodology: we work closely with your team to identify and map processes with the highest automation potential. We analyze workflows, detect bottlenecks, and design solutions perfectly aligned with your business objectives.',
        },
        {
          title: 'Process Analytics',
          desc: 'We evaluate the current performance of your processes and establish key metrics that guide the automation design. This detailed analysis allows us to understand operational patterns and accurately predict how automation will impact your company\'s overall efficiency.',
        },
        {
          title: 'Testing',
          desc: 'Before deploying any solution, we conduct rigorous tests across different scenarios and operational conditions. We identify potential failure points and resolve them before launch, ensuring a frictionless integration from day one.',
        },
        {
          title: 'Implementación',
          desc: 'We deploy automated solutions to production with a smooth, uninterrupted transition. We provide ongoing support and training so your team adopts the new tools quickly — minimizing downtime and maximizing ROI from the very first moment.',
        },
      ],
      faq_esp: [
        {
          question: '¿Qué procesos de mi empresa se pueden automatizar?',
          answer: 'Cualquier proceso repetitivo con potencial de mejora. Lo identificamos en la etapa de Discovery, donde trabajamos con tu equipo para mapear flujos de trabajo y detectar cuellos de botella.',
        },
        {
          question: '¿Cómo saben qué proceso conviene automatizar primero?',
          answer: 'A través de nuestro Process Analytics: evaluamos el rendimiento actual de tus procesos y establecemos métricas clave que guían el diseño de la automatización antes de implementar nada.',
        },
        {
          question: '¿La automatización reemplaza a mi equipo o libera tiempo?',
          answer: 'El objetivo es eliminar tareas repetitivas para que tu equipo se enfoque en actividades estratégicas que generan valor real, no reemplazar personas.',
        },
        {
          question: '¿Prueban la solución antes de implementarla?',
          answer: 'Sí, llevamos a cabo pruebas rigurosas bajo distintos escenarios y condiciones operativas antes del lanzamiento, para garantizar una integración sin fricciones.',
        },
        {
          question: '¿Dan capacitación a mi equipo después de implementar la solución?',
          answer: 'Sí, proporcionamos soporte continuo y capacitación para una adopción rápida, minimizando el tiempo de inactividad y maximizando el retorno de la inversión.',
        },
      ],
      faq_eng: [
        {
          question: 'Which of my company\'s processes can be automated?',
          answer: 'Any repetitive process with room for improvement. We identify it during the Discovery stage, working with your team to map workflows and detect bottlenecks.',
        },
        {
          question: 'How do you know which process to automate first?',
          answer: 'Through our Process Analytics: we evaluate the current performance of your processes and set key metrics that guide the automation design before implementing anything.',
        },
        {
          question: 'Does automation replace my team or free up their time?',
          answer: 'The goal is to eliminate repetitive tasks so your team can focus on strategic, value-generating activities, not to replace people.',
        },
        {
          question: 'Do you test the solution before implementing it?',
          answer: 'Yes, we run rigorous tests across different scenarios and operational conditions before launch, to ensure a frictionless integration.',
        },
        {
          question: 'Do you train my team after implementing the solution?',
          answer: 'Yes, we provide ongoing support and training for fast adoption, minimizing downtime and maximizing ROI.',
        },
      ],
    },

    4: {
      title_esp: 'Inteligencia Artificial',
      title_eng: 'Artificial Intelligence',
      subtitle_esp: 'Soluciones de IA que transforman datos en decisiones y ventajas competitivas.',
      subtitle_eng: 'AI solutions that transform data into decisions and competitive advantages.',
      intro_esp: 'Implementamos soluciones de inteligencia artificial que transforman la manera en que las empresas operan y toman decisiones. Nuestras soluciones están diseñadas para aprovechar el poder de los datos y la tecnología avanzada, permitiendo a las organizaciones ser más ágiles, eficientes y competitivas. Desde la predicción de tendencias hasta la automatización de procesos complejos, la IA abre nuevas oportunidades de crecimiento en todos los aspectos de tu negocio.',
      intro_eng: 'We implement artificial intelligence solutions that transform the way companies operate and make decisions. Our solutions are designed to harness the power of data and advanced technology, enabling organizations to become more agile, efficient, and competitive. From trend prediction to complex process automation, AI opens new growth opportunities across every aspect of your business.',
      icon: '../../assets/chain/Asset 19.png',
      features_esp: [
        {
          title: 'Modelos Predictivos',
          desc: 'Desarrollamos modelos que anticipan comportamientos y resultados futuros a partir del análisis de grandes volúmenes de datos. Esenciales para la toma de decisiones estratégicas: optimizan inventarios, predicen la demanda, identifican tendencias y mitigan riesgos, permitiéndote planificar con mayor precisión.',
        },
        {
          title: 'Automatización con IA',
          desc: 'La inteligencia artificial lleva la automatización al siguiente nivel: procesos que no solo son más rápidos y eficientes, sino también más inteligentes. Implementamos soluciones que aprenden y se adaptan continuamente, desde la automatización de tareas rutinarias hasta la toma de decisiones autónomas en tiempo real.',
        },
        {
          title: 'Análisis de Datos Avanzado',
          desc: 'Aplicamos técnicas de machine learning y deep learning para extraer conocimientos valiosos de grandes conjuntos de datos. Descubrimos patrones ocultos, optimizamos operaciones y mejoramos la toma de decisiones en todas las áreas, ayudando a tu empresa a anticipar tendencias del mercado.',
        },
        {
          title: 'Personalización de Experiencia',
          desc: 'Con IA, personalizamos la experiencia del cliente a un nivel sin precedentes. Desarrollamos soluciones que analizan el comportamiento y las preferencias de los usuarios en tiempo real, permitiendo ofrecer recomendaciones precisas, contenido adaptado y experiencias únicas que incrementan la lealtad.',
        },
      ],
      features_eng: [
        {
          title: 'Predictive Models',
          desc: 'We develop models that anticipate future behaviors and outcomes based on the analysis of large data volumes. Essential for strategic decision-making: they optimize inventories, predict demand, identify trends, and mitigate risks — allowing you to plan with greater precision.',
        },
        {
          title: 'AI Automation',
          desc: 'Artificial intelligence takes automation to the next level: processes that are not only faster and more efficient, but also smarter. We implement solutions that continuously learn and adapt — from automating routine tasks to real-time autonomous decision-making.',
        },
        {
          title: 'Advanced Data Analytics',
          desc: 'We apply machine learning and deep learning techniques to extract valuable insights from large datasets. We uncover hidden patterns, optimize operations, and improve decision-making across all areas — helping your company anticipate market trends.',
        },
        {
          title: 'Experience Personalization',
          desc: 'With AI, we personalize the customer experience at an unprecedented level. We develop solutions that analyze user behavior and preferences in real time, enabling precise recommendations, tailored content, and unique experiences that increase long-term loyalty.',
        },
      ],
      faq_esp: [
        {
          question: '¿Por dónde empieza una pyme a implementar IA?',
          answer: 'Recomendamos arrancar por casos acotados y de alto impacto, como automatización con IA de tareas rutinarias o modelos predictivos sobre datos que ya tenés, antes de escalar a soluciones más complejas.',
        },
        {
          question: '¿Qué es un modelo predictivo y para qué sirve?',
          answer: 'Es un modelo que anticipa comportamientos y resultados futuros a partir del análisis de datos: optimiza inventarios, predice demanda, identifica tendencias y mitiga riesgos para que puedas planificar con mayor precisión.',
        },
        {
          question: '¿La IA sirve para empresas chicas o solo para grandes corporaciones?',
          answer: 'Sirve para cualquier empresa que tenga datos y procesos que optimizar. Diseñamos soluciones a medida del volumen y la madurez digital de cada negocio.',
        },
        {
          question: '¿Qué diferencia hay entre automatización tradicional e IA?',
          answer: 'La IA lleva la automatización a otro nivel: procesos que no solo son más rápidos, sino que aprenden y se adaptan continuamente, hasta tomar decisiones autónomas en tiempo real.',
        },
        {
          question: '¿Qué datos necesito tener para empezar?',
          answer: 'Partimos de los datos que ya generás en tu operación diaria. En la etapa de análisis evaluamos qué fuentes tenés disponibles y cómo aprovecharlas.',
        },
      ],
      faq_eng: [
        {
          question: 'Where should a small business start with AI?',
          answer: 'We recommend starting with narrow, high-impact use cases, like AI automation of routine tasks or predictive models on data you already have, before scaling to more complex solutions.',
        },
        {
          question: 'What is a predictive model and what is it for?',
          answer: 'It\'s a model that anticipates future behaviors and outcomes based on data analysis: it optimizes inventories, predicts demand, identifies trends, and mitigates risks so you can plan with greater precision.',
        },
        {
          question: 'Is AI useful for small businesses or only large corporations?',
          answer: 'It\'s useful for any company with data and processes to optimize. We design solutions tailored to each business\'s volume and digital maturity.',
        },
        {
          question: 'What\'s the difference between traditional automation and AI?',
          answer: 'AI takes automation to another level: processes that are not only faster, but that continuously learn and adapt, up to making autonomous decisions in real time.',
        },
        {
          question: 'What data do I need to have to get started?',
          answer: 'We start with the data you already generate in your day-to-day operation. During the analysis stage we assess what sources you have available and how to leverage them.',
        },
      ],
    },

    5: {
      title_esp: 'Robotización',
      title_eng: 'Robotics',
      subtitle_esp: 'Robots de software que trabajan 24/7, sin errores y sin descanso.',
      subtitle_eng: 'Software robots that work 24/7, error-free and non-stop.',
      intro_esp: 'Diseñamos e implementamos robots de software (RPA) que ejecutan las tareas repetitivas de tu operación tal como lo haría una persona, pero las 24 horas, sin errores y a una fracción del costo. Cargan datos, concilian información entre sistemas, descargan y procesan documentos, generan reportes y disparan alertas, liberando a tu equipo para el trabajo que realmente genera valor.',
      intro_eng: 'We design and implement software robots (RPA) that perform the repetitive tasks of your operation just like a person would, but around the clock, error-free and at a fraction of the cost. They enter data, reconcile information across systems, download and process documents, generate reports and trigger alerts, freeing your team for the work that truly creates value.',
      icon: '',
      features_esp: [
        {
          title: 'Relevamiento de procesos',
          desc: 'Mapeamos junto a tu equipo las tareas manuales, repetitivas y basadas en reglas que consumen más horas. Priorizamos las de mayor retorno para que el primer robot genere impacto desde el día uno.',
        },
        {
          title: 'Robots de software (RPA)',
          desc: 'Construimos bots que operan sobre tus sistemas actuales (ERPs, planillas, portales web, correo) sin necesidad de reemplazarlos. Trabajan de forma desatendida, con horarios programados o disparados por eventos.',
        },
        {
          title: 'Integración de sistemas',
          desc: 'Conectamos aplicaciones que no se hablan entre sí mediante APIs o, cuando no existen, mediante robots que replican la interacción humana. Los datos fluyen solos entre sistemas, sin doble carga.',
        },
        {
          title: 'Monitoreo y mantenimiento',
          desc: 'Cada robot queda instrumentado con logs, alertas y tableros de control. Sabés qué hizo, cuándo y con qué resultado, y nosotros lo mantenemos ante cambios en tus sistemas.',
        },
      ],
      features_eng: [
        {
          title: 'Process discovery',
          desc: 'Together with your team, we map the manual, repetitive, rule-based tasks that consume the most hours. We prioritize those with the highest return so the first robot delivers impact from day one.',
        },
        {
          title: 'Software robots (RPA)',
          desc: 'We build bots that operate on top of your current systems (ERPs, spreadsheets, web portals, email) without replacing them. They run unattended, on a schedule or triggered by events.',
        },
        {
          title: 'Systems integration',
          desc: 'We connect applications that don\'t talk to each other through APIs or, when none exist, through robots that replicate human interaction. Data flows between systems on its own, with no double entry.',
        },
        {
          title: 'Monitoring & maintenance',
          desc: 'Every robot is instrumented with logs, alerts and control dashboards. You know what it did, when and with what result, and we maintain it as your systems change.',
        },
      ],
      faq_esp: [
        {
          question: '¿Qué es la robotización de procesos (RPA)?',
          answer: 'Es el uso de robots de software que imitan las acciones de una persona frente a la computadora (hacer clics, copiar datos, completar formularios, leer documentos) para ejecutar tareas repetitivas de forma automática y sin errores.',
        },
        {
          question: '¿Qué tareas puede hacer un robot?',
          answer: 'Cualquier tarea repetitiva y basada en reglas: carga de facturas, conciliaciones bancarias, descarga de reportes de portales, actualización de stock y precios, envío de notificaciones y traspaso de datos entre sistemas.',
        },
        {
          question: '¿Tengo que cambiar mis sistemas actuales?',
          answer: 'No. Los robots trabajan sobre las herramientas que ya usás, incluso sistemas antiguos sin API, por lo que la implementación es rápida y no interrumpe tu operación.',
        },
        {
          question: '¿Qué pasa si un robot falla?',
          answer: 'Cada robot tiene monitoreo y alertas: si algo cambia o falla, se notifica al instante y el caso queda registrado para resolverlo. Además ofrecemos mantenimiento continuo.',
        },
        {
          question: '¿En cuánto tiempo se ve el retorno?',
          answer: 'Un primer robot suele estar operativo en pocas semanas, y como trabaja 24/7 sin errores, el ahorro de horas se ve desde el primer mes.',
        },
      ],
      faq_eng: [
        {
          question: 'What is robotic process automation (RPA)?',
          answer: 'It\'s the use of software robots that mimic a person\'s actions at a computer (clicking, copying data, filling in forms, reading documents) to perform repetitive tasks automatically and without errors.',
        },
        {
          question: 'What tasks can a robot do?',
          answer: 'Any repetitive, rule-based task: invoice entry, bank reconciliations, downloading reports from portals, updating stock and prices, sending notifications and moving data between systems.',
        },
        {
          question: 'Do I have to change my current systems?',
          answer: 'No. Robots work on top of the tools you already use, even legacy systems without an API, so implementation is fast and doesn\'t disrupt your operation.',
        },
        {
          question: 'What happens if a robot fails?',
          answer: 'Every robot has monitoring and alerts: if something changes or fails, you are notified instantly and the case is logged to be resolved. We also offer ongoing maintenance.',
        },
        {
          question: 'How soon will I see a return?',
          answer: 'A first robot is usually up and running within a few weeks, and since it works 24/7 without errors, the hours saved show from the first month.',
        },
      ],
    },
  };

  // ─── Constructor (igual al tuyo original) ──────────────────────────────────
  constructor(
    private authService: AuthService,
    private router: Router,
    private title: Title,
    private meta: Meta,
    private rutaActiva: ActivatedRoute,
    private seoService: SeoService,
  ) {}

  // ─── SEO por servicio: título y descripción apuntando a Rosario ────────────
  private seoByService: Record<number, { title: string; description: string }> = {
    1: {
      title: 'Desarrollo de E-Commerce en Rosario | Patagonia Chain',
      description: 'Plataformas de e-commerce a medida en Rosario, Argentina. Diseño UX/UI, integraciones y CMS personalizado para vender online y crecer globalmente.',
    },
    2: {
      title: 'Desarrollo Web a Medida en Rosario | Patagonia Chain',
      description: 'Desarrollo web a medida en Rosario, Argentina: sitios institucionales, aplicaciones y plataformas personalizadas para conectar tu empresa con el mundo.',
    },
    3: {
      title: 'Desarrollo de Software en Rosario | Patagonia Chain',
      description: 'Desarrollo de software y automatización de procesos en Rosario, Argentina. Reducí errores humanos y aumentá la productividad con tecnología a medida.',
    },
    4: {
      title: 'Inteligencia Artificial en Rosario | Patagonia Chain',
      description: 'Soluciones de inteligencia artificial en Rosario, Argentina. Modelos predictivos, automatización con IA y análisis de datos avanzado para tu negocio.',
    },
    5: {
      title: 'Robotización de Procesos (RPA) en Rosario | Patagonia Chain',
      description: 'Robotización de procesos en Rosario, Argentina. Robots de software (RPA) que trabajan 24/7, integran tus sistemas y eliminan tareas repetitivas y errores.',
    },
  };

  // ─── ngOnInit (estructura idéntica a la original, con lógica nueva) ─────────
  ngOnInit(): void {
    // Suscripto a los params (no snapshot) para que también funcione al navegar
    // de un servicio a otro sin recrear el componente (links del footer).
    this.rutaActiva.params.subscribe(params => {
      this.event = params['id'];
      this.ecomm = 0;
      if (this.event === 'ecommerce')              this.ecomm = 1;
      if (this.event === 'desarrollo-web')         this.ecomm = 2;
      if (this.event === 'automatizacion')         this.ecomm = 3;
      if (this.event === 'inteligencia-artificial') this.ecomm = 4;
      if (this.event === 'robotizacion')           this.ecomm = 5;
      try { window.scroll(0, 0); } catch {}
      this.openFaqIndex = -1;

      // SEO: título y descripción propios de cada servicio
      const seo = this.seoByService[this.ecomm];
      if (seo) {
        this.title.setTitle(seo.title);
        this.meta.updateTag({ name: 'description', content: seo.description });
        this.meta.updateTag({ property: 'og:title', content: seo.title });
        this.meta.updateTag({ property: 'og:description', content: seo.description });
      }

      // SEO: FAQPage estructurado con las preguntas propias del servicio
      this.seoService.setFaqSchema(this.services[this.ecomm]?.faq_esp ?? []);
    });

    // Idioma desde sessionStorage — igual que el componente principal
    let savedLang: 'ESP' | 'ENG' | null = null;
    try { savedLang = sessionStorage.getItem('idioma') as 'ESP' | 'ENG' | null; } catch {}
    this.lang = savedLang ?? 'ESP';
    this.t = this.translations[this.lang];

    // AOS — igual que tu código original
    AOS.init({ duration: 800, delay: 0 });
  }

  // ─── Navegación del breadcrumb ──────────────────────────────────────────────
  irr(s: string): void {
    if (s === 'home') this.router.navigate(['/']);
  }

  // ─── Navegación del footer: ir a otro servicio ──────────────────────────────
  ir(url: string): void {
    this.router.navigate(['/servicio/' + url]);
  }

  goMethod(): void {
    this.router.navigate(['/como-trabajamos']);
  }

  // ─── Navegación del footer: la sección vive en la home, así que navega
  // y deja marcada la posición para que la home haga scroll una vez montada.
  goHome(id: string): void {
    localStorage.setItem('pos', id);
    this.router.navigate(['/']);
  }

  // ─── Helpers para el template ──────────────────────────────────────────────
  getCurrentTitle(): string {
    const s = this.services[this.ecomm];
    if (!s) return '';
    return this.lang === 'ESP' ? s.title_esp : s.title_eng;
  }

  getCurrentSubtitle(): string {
    const s = this.services[this.ecomm];
    if (!s) return '';
    return this.lang === 'ESP' ? s.subtitle_esp : s.subtitle_eng;
  }

  getCurrentIntro(): string {
    const s = this.services[this.ecomm];
    if (!s) return '';
    return this.lang === 'ESP' ? s.intro_esp : s.intro_eng;
  }

  getCurrentIcon(): string {
    return this.services[this.ecomm]?.icon ?? '';
  }

  getCurrentFeatures(): Feature[] {
    const s = this.services[this.ecomm];
    if (!s) return [];
    return this.lang === 'ESP' ? s.features_esp : s.features_eng;
  }

  getCurrentFaqs(): FaqQA[] {
    const s = this.services[this.ecomm];
    if (!s) return [];
    return this.lang === 'ESP' ? s.faq_esp : s.faq_eng;
  }

  toggleFaq(i: number): void {
    this.openFaqIndex = this.openFaqIndex === i ? -1 : i;
  }

  ngOnDestroy(): void {
    this.seoService.removeFaqSchema();
  }

  // Detecta cambios de idioma en tiempo real (mismo patrón que el componente principal)
  ngDoCheck(): void {
    try {
      const current = sessionStorage.getItem('idioma') as 'ESP' | 'ENG' | null;
      const next = current ?? 'ESP';
      if (next !== this.lang) {
        this.lang = next;
        this.t = this.translations[this.lang];
      }
    } catch {}
  }
}
